/**
 * 数据管理器
 * 
 * 统一管理所有数据操作，包括：
 * 1. 数据的增删改查
 * 2. 数据持久化
 * 3. 数据验证
 * 4. 数据迁移
 */

import { encryptRC4, decryptRC4 } from '../utils/rc4.js';
import { migrateToLatestVersion, exportData, cleanupData, DATA_VERSIONS } from '../utils/migration.js';
import { allocateRestTime, updateOvertimeRecordsRemainingTime } from './ConsumptionAllocator.js';
import { calculateOvertimeHours, calculateWorkingHours, calculateRestHours, calculateActualHours } from './TimeCalculator.js';

// 计算类型常量
export const CALCULATION_TYPE = {
  WORKING_HOURS: 'working', // 按工作时间8小时计算（扣除午休）
  ACTUAL_HOURS: 'actual'    // 按实际时间计算（不扣除午休）
};

/**
 * 数据管理器类
 */
export class DataManager {
  constructor() {
    this.data = this.createDefaultData();
    this.listeners = new Set();
  }

  /**
   * 创建默认数据结构
   */
  createDefaultData() {
    return {
      version: DATA_VERSIONS.V2_0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      overtimeRecords: [],
      consumptionRecords: [],
      restRecords: [],
      annualLeave: {
        totalDays: 0,
        totalHours: 0,
        usedHours: 0,
        remainingHours: 0
      },
      annualLeaveRecords: []
    };
  }

  /**
   * 添加数据变更监听器
   */
  addListener(listener) {
    this.listeners.add(listener);
  }

  /**
   * 移除数据变更监听器
   */
  removeListener(listener) {
    this.listeners.delete(listener);
  }

  /**
   * 通知所有监听器数据已变更
   */
  notifyListeners(changeType, data) {
    this.data.updatedAt = new Date().toISOString();
    this.listeners.forEach(listener => {
      try {
        listener(changeType, data, this.data);
      } catch (error) {
        console.error('监听器执行错误:', error);
      }
    });
  }

  /**
   * 获取所有数据
   */
  getAllData() {
    return { ...this.data };
  }

  /**
   * 设置所有数据
   */
  setAllData(newData) {
    this.data = migrateToLatestVersion(newData);
    this.notifyListeners('DATA_REPLACED', this.data);
  }

  // ==================== 加班记录管理 ====================

  /**
   * 添加加班记录
   */
  addOvertimeRecord(startTime, endTime) {
    const duration = calculateOvertimeHours(startTime, endTime);
    
    if (duration <= 0) {
      throw new Error('加班时间必须大于0');
    }

    const record = {
      id: this.generateId(),
      startTime: new Date(startTime).toISOString(),
      endTime: new Date(endTime).toISOString(),
      duration,
      remainingTime: duration,
      createdAt: new Date().toISOString()
    };

    this.data.overtimeRecords.push(record);
    this.saveToLocalStorage(); // 自动保存
    this.notifyListeners('OVERTIME_ADDED', record);

    return record;
  }

  /**
   * 更新加班记录
   */
  updateOvertimeRecord(id, updates) {
    const index = this.data.overtimeRecords.findIndex(r => r.id === id);
    
    if (index === -1) {
      throw new Error('加班记录不存在');
    }

    const oldRecord = this.data.overtimeRecords[index];
    const newRecord = { ...oldRecord, ...updates };

    // 如果时间发生变化，重新计算时长
    if (updates.startTime || updates.endTime) {
      newRecord.duration = calculateOvertimeHours(newRecord.startTime, newRecord.endTime);
      
      if (newRecord.duration <= 0) {
        throw new Error('加班时间必须大于0');
      }
    }

    this.data.overtimeRecords[index] = newRecord;
    
    // 重新计算所有加班记录的剩余时间
    this.data.overtimeRecords = updateOvertimeRecordsRemainingTime(
      this.data.overtimeRecords,
      this.data.consumptionRecords
    );

    this.saveToLocalStorage(); // 自动保存
    this.saveToLocalStorage(); // 自动保存
    this.notifyListeners('OVERTIME_UPDATED', newRecord);

    return newRecord;
  }

  /**
   * 更新调休记录
   * 需要重新分配消费记录以确保数据联动
   */
  updateRestRecord(id, updates) {
    const index = this.data.restRecords.findIndex(r => r.id === id);

    if (index === -1) {
      throw new Error('调休记录不存在');
    }

    const oldRecord = this.data.restRecords[index];

    // 构建新的记录
    const newStartTime = updates.txbegintime || oldRecord.txbegintime;
    const newEndTime = updates.txendtime || oldRecord.txendtime;
    const calculationType = updates.calculationType || oldRecord.calculationType || CALCULATION_TYPE.WORKING_HOURS;

    // 根据计算类型选择计算函数
    const newDuration = calculationType === CALCULATION_TYPE.ACTUAL_HOURS
      ? calculateActualHours(newStartTime, newEndTime)
      : calculateRestHours(newStartTime, newEndTime);

    if (newDuration <= 0) {
      throw new Error('调休时间必须大于0');
    }

    // 1. 删除旧的消费记录并归还时间
    const oldConsumptions = this.data.consumptionRecords.filter(c =>
      c.restStartTime === oldRecord.txbegintime &&
      c.restEndTime === oldRecord.txendtime
    );

    // 删除旧的年假记录（调休自动扣除的）
    const oldAnnualLeaveRecords = this.data.annualLeaveRecords.filter(record =>
      record.source === '调休自动扣除' &&
      record.relatedRestTime === `${oldRecord.txbegintime}_${oldRecord.txendtime}`
    );

    oldAnnualLeaveRecords.forEach(annualLeaveRecord => {
      const annualLeaveIndex = this.data.annualLeaveRecords.findIndex(r => r.id === annualLeaveRecord.id);
      if (annualLeaveIndex !== -1) {
        this.data.annualLeaveRecords.splice(annualLeaveIndex, 1);
      }
    });

    // 删除旧的消费记录并归还年假时间
    oldConsumptions.forEach(consumption => {
      const consumptionIndex = this.data.consumptionRecords.findIndex(c => c.id === consumption.id);
      if (consumptionIndex !== -1) {
        this.data.consumptionRecords.splice(consumptionIndex, 1);
      }
      // 如果是年假消费，归还年假时间
      if (consumption.type === '年假') {
        this.data.annualLeave.usedHours -= consumption.deductionTime;
        this.data.annualLeave.remainingHours += consumption.deductionTime;
      }
    });

    // 重新计算加班记录的剩余时间（归还加班时间）
    this.data.overtimeRecords = updateOvertimeRecordsRemainingTime(
      this.data.overtimeRecords,
      this.data.consumptionRecords
    );

    // 2. 构建新的调休请求并重新分配
    const newRecord = {
      ...oldRecord,
      txbegintime: new Date(newStartTime).toISOString(),
      txendtime: new Date(newEndTime).toISOString(),
      duration: newDuration,
      calculationType
    };

    // 重新分配时间
    const allocationResult = allocateRestTime(
      newRecord,
      this.data.overtimeRecords,
      this.data.annualLeave
    );

    if (!allocationResult.success) {
      // 如果分配失败，需要恢复旧的状态
      // 重新添加旧的消费记录
      this.data.consumptionRecords.push(...oldConsumptions);
      // 重新添加旧的年假记录
      this.data.annualLeaveRecords.push(...oldAnnualLeaveRecords);
      // 恢复年假使用情况
      oldConsumptions.forEach(consumption => {
        if (consumption.type === '年假') {
          this.data.annualLeave.usedHours += consumption.deductionTime;
          this.data.annualLeave.remainingHours -= consumption.deductionTime;
        }
      });
      // 重新计算加班记录的剩余时间
      this.data.overtimeRecords = updateOvertimeRecordsRemainingTime(
        this.data.overtimeRecords,
        this.data.consumptionRecords
      );
      throw new Error(allocationResult.error);
    }

    // 3. 更新调休记录
    this.data.restRecords[index] = newRecord;

    // 4. 添加新的消费记录
    this.data.consumptionRecords.push(...allocationResult.consumptionRecords);

    // 5. 更新加班记录的剩余时间
    this.data.overtimeRecords = updateOvertimeRecordsRemainingTime(
      this.data.overtimeRecords,
      this.data.consumptionRecords
    );

    // 6. 更新年假使用情况
    const annualLeaveUsed = allocationResult.summary.annualLeaveHoursUsed;
    if (annualLeaveUsed > 0) {
      this.data.annualLeave.usedHours += annualLeaveUsed;
      this.data.annualLeave.remainingHours -= annualLeaveUsed;

      // 创建新的年假记录
      const annualLeaveRecord = {
        id: this.generateId(),
        startTime: newRecord.txbegintime,
        endTime: newRecord.txendtime,
        duration: annualLeaveUsed,
        type: '年假',
        source: '调休自动扣除',
        relatedRestTime: `${newRecord.txbegintime}_${newRecord.txendtime}`,
        createdAt: new Date().toISOString()
      };
      this.data.annualLeaveRecords.push(annualLeaveRecord);
    }

    this.saveToLocalStorage();
    this.notifyListeners('REST_UPDATED', {
      restRecord: newRecord,
      consumptionRecords: allocationResult.consumptionRecords,
      summary: allocationResult.summary
    });

    return newRecord;
  }

  /**
   * 更新年假记录
   */
  updateAnnualLeaveRecord(id, updates) {
    const index = this.data.annualLeaveRecords.findIndex(r => r.id === id);

    if (index === -1) {
      throw new Error('年假记录不存在');
    }

    const oldRecord = this.data.annualLeaveRecords[index];
    const oldDuration = oldRecord.duration;
    const newRecord = { ...oldRecord, ...updates };

    // 如果时间发生变化，重新计算时长
    if (updates.startTime || updates.endTime) {
      // 获取计算类型，兼容旧数据（没有calculationType字段时默认使用工作时间计算）
      const calculationType = updates.calculationType || oldRecord.calculationType || CALCULATION_TYPE.WORKING_HOURS;
      newRecord.calculationType = calculationType;

      // 根据计算类型选择计算函数
      newRecord.duration = calculationType === CALCULATION_TYPE.ACTUAL_HOURS
        ? calculateActualHours(newRecord.startTime, newRecord.endTime)
        : calculateWorkingHours(newRecord.startTime, newRecord.endTime);

      if (newRecord.duration <= 0) {
        throw new Error('年假时间必须大于0');
      }

      // 计算时长变化
      const durationChange = newRecord.duration - oldDuration;

      // 如果时长增加，检查年假余额是否足够
      if (durationChange > 0) {
        if (durationChange > this.data.annualLeave.remainingHours) {
          throw new Error(`年假余额不足。需要额外${durationChange.toFixed(1)}小时，剩余${this.data.annualLeave.remainingHours.toFixed(1)}小时`);
        }
      }

      // 更新年假使用情况
      if (durationChange !== 0) {
        this.data.annualLeave.usedHours += durationChange;
        this.data.annualLeave.remainingHours -= durationChange;

        // 如果是调休自动扣除的记录，还需要更新相关的消费记录
        if (oldRecord.source === '调休自动扣除' && oldRecord.relatedRestTime) {
          const [restStartTime, restEndTime] = oldRecord.relatedRestTime.split('_');

          // 查找并更新相关的消费记录
          const relatedConsumption = this.data.consumptionRecords.find(c =>
            c.type === '年假' &&
            c.restStartTime === restStartTime &&
            c.restEndTime === restEndTime
          );

          if (relatedConsumption) {
            relatedConsumption.deductionTime = newRecord.duration;
            relatedConsumption.consumptionStartTime = newRecord.startTime;
            relatedConsumption.consumptionEndTime = newRecord.endTime;
          }
        }
      }
    }

    this.data.annualLeaveRecords[index] = newRecord;
    this.saveToLocalStorage(); // 自动保存
    this.notifyListeners('ANNUAL_LEAVE_RECORD_UPDATED', {
      annualLeaveRecord: newRecord,
      oldRecord: oldRecord,
      durationChange: newRecord.duration - oldDuration,
      updatedAnnualLeave: this.data.annualLeave
    });

    return newRecord;
  }

  /**
   * 删除加班记录
   */
  deleteOvertimeRecord(id) {
    const index = this.data.overtimeRecords.findIndex(r => r.id === id);
    
    if (index === -1) {
      throw new Error('加班记录不存在');
    }

    // 检查是否有相关的消费记录
    const relatedConsumptions = this.data.consumptionRecords.filter(c => c.overtimeId === id);
    
    if (relatedConsumptions.length > 0) {
      throw new Error('该加班记录已被使用，无法删除。请先删除相关的调休记录。');
    }

    const deletedRecord = this.data.overtimeRecords.splice(index, 1)[0];
    this.saveToLocalStorage(); // 自动保存
    this.notifyListeners('OVERTIME_DELETED', deletedRecord);

    return deletedRecord;
  }

  // ==================== 调休记录管理 ====================

  /**
   * 添加调休记录
   * @param {Date|string} startTime - 开始时间
   * @param {Date|string} endTime - 结束时间
   * @param {string} calculationType - 计算类型：'working'（按工作时间）或 'actual'（按实际时间）
   */
  addRestRecord(startTime, endTime, calculationType = CALCULATION_TYPE.WORKING_HOURS) {
    // 根据计算类型选择计算函数
    const duration = calculationType === CALCULATION_TYPE.ACTUAL_HOURS
      ? calculateActualHours(startTime, endTime)
      : calculateRestHours(startTime, endTime);

    if (duration <= 0) {
      throw new Error('调休时间必须大于0');
    }

    const restRequest = {
      id: this.generateId(),
      txbegintime: new Date(startTime).toISOString(),
      txendtime: new Date(endTime).toISOString(),
      duration,
      calculationType, // 保存计算类型以便兼容
      createdAt: new Date().toISOString()
    };

    // 分配时间
    const allocationResult = allocateRestTime(
      restRequest,
      this.data.overtimeRecords,
      this.data.annualLeave
    );

    if (!allocationResult.success) {
      throw new Error(allocationResult.error);
    }

    // 添加调休记录
    this.data.restRecords.push(restRequest);

    // 添加消费记录
    this.data.consumptionRecords.push(...allocationResult.consumptionRecords);

    // 更新加班记录的剩余时间
    this.data.overtimeRecords = updateOvertimeRecordsRemainingTime(
      this.data.overtimeRecords,
      this.data.consumptionRecords
    );

    // 更新年假使用情况
    const annualLeaveUsed = allocationResult.summary.annualLeaveHoursUsed;
    if (annualLeaveUsed > 0) {
      this.data.annualLeave.usedHours += annualLeaveUsed;
      this.data.annualLeave.remainingHours -= annualLeaveUsed;

      // 创建年假记录（显示在年假记录列表中）
      const annualLeaveRecord = {
        id: this.generateId(),
        startTime: restRequest.txbegintime,
        endTime: restRequest.txendtime,
        duration: annualLeaveUsed,
        type: '年假',
        source: '调休自动扣除', // 标记来源
        relatedRestTime: `${restRequest.txbegintime}_${restRequest.txendtime}`, // 关联的调休时间
        createdAt: new Date().toISOString()
      };

      // 添加到年假记录列表
      this.data.annualLeaveRecords.push(annualLeaveRecord);
    }

    this.saveToLocalStorage(); // 自动保存
    this.notifyListeners('REST_ADDED', {
      restRecord: restRequest,
      consumptionRecords: allocationResult.consumptionRecords,
      summary: allocationResult.summary
    });

    return {
      restRecord: restRequest,
      allocationResult
    };
  }

  /**
   * 删除调休记录
   */
  deleteRestRecord(id) {
    const restIndex = this.data.restRecords.findIndex(r => r.id === id);
    
    if (restIndex === -1) {
      throw new Error('调休记录不存在');
    }

    const restRecord = this.data.restRecords[restIndex];

    // 找到相关的消费记录
    const relatedConsumptions = this.data.consumptionRecords.filter(c =>
      c.restStartTime === restRecord.txbegintime &&
      c.restEndTime === restRecord.txendtime
    );

    // 删除调休记录
    this.data.restRecords.splice(restIndex, 1);

    // 查找并删除相关的年假记录（调休自动扣除的）
    const relatedAnnualLeaveRecords = this.data.annualLeaveRecords.filter(record =>
      record.source === '调休自动扣除' &&
      record.relatedRestTime === `${restRecord.txbegintime}_${restRecord.txendtime}`
    );

    // 删除相关的年假记录（不在这里归还年假时间，避免双重归还）
    relatedAnnualLeaveRecords.forEach(annualLeaveRecord => {
      const annualLeaveIndex = this.data.annualLeaveRecords.findIndex(r => r.id === annualLeaveRecord.id);
      if (annualLeaveIndex !== -1) {
        this.data.annualLeaveRecords.splice(annualLeaveIndex, 1);
      }
    });

    // 删除相关的消费记录并归还时间
    relatedConsumptions.forEach(consumption => {
      const consumptionIndex = this.data.consumptionRecords.findIndex(c => c.id === consumption.id);
      if (consumptionIndex !== -1) {
        this.data.consumptionRecords.splice(consumptionIndex, 1);
      }

      // 如果是年假消费，需要恢复年假时间
      if (consumption.type === '年假') {
        this.data.annualLeave.usedHours -= consumption.deductionTime;
        this.data.annualLeave.remainingHours += consumption.deductionTime;
      }
    });

    // 重新计算加班记录的剩余时间
    this.data.overtimeRecords = updateOvertimeRecordsRemainingTime(
      this.data.overtimeRecords,
      this.data.consumptionRecords
    );

    this.saveToLocalStorage(); // 自动保存
    this.notifyListeners('REST_DELETED', {
      restRecord,
      deletedConsumptions: relatedConsumptions,
      deletedAnnualLeaveRecords: relatedAnnualLeaveRecords
    });

    return restRecord;
  }

  // ==================== 年假管理 ====================

  /**
   * 设置年假总天数
   */
  setAnnualLeaveDays(totalDays) {
    if (totalDays < 0) {
      throw new Error('年假天数不能为负数');
    }

    // eslint-disable-next-line no-unused-vars
    const _oldTotalHours = this.data.annualLeave.totalHours;
    const newTotalHours = totalDays * 8;
    const usedHours = this.data.annualLeave.usedHours;

    this.data.annualLeave = {
      totalDays,
      totalHours: newTotalHours,
      usedHours,
      remainingHours: newTotalHours - usedHours
    };

    this.saveToLocalStorage(); // 自动保存
    this.notifyListeners('ANNUAL_LEAVE_UPDATED', this.data.annualLeave);

    return this.data.annualLeave;
  }

  /**
   * 设置年假总小时数（自定义小时）
   */
  setAnnualLeaveHours(totalHours) {
    if (totalHours < 0) {
      throw new Error('年假小时数不能为负数');
    }

    const usedHours = this.data.annualLeave.usedHours;
    const totalDays = Math.floor(totalHours / 8); // 计算对应天数（向下取整）

    this.data.annualLeave = {
      totalDays,
      totalHours,
      usedHours,
      remainingHours: totalHours - usedHours
    };

    this.saveToLocalStorage(); // 自动保存
    this.notifyListeners('ANNUAL_LEAVE_UPDATED', this.data.annualLeave);

    return this.data.annualLeave;
  }

  /**
   * 添加年假记录
   * @param {Date|string} startTime - 开始时间
   * @param {Date|string} endTime - 结束时间
   * @param {string} calculationType - 计算类型：'working'（按工作时间）或 'actual'（按实际时间）
   */
  addAnnualLeaveRecord(startTime, endTime, calculationType = CALCULATION_TYPE.WORKING_HOURS) {
    // 根据计算类型选择计算函数
    const duration = calculationType === CALCULATION_TYPE.ACTUAL_HOURS
      ? calculateActualHours(startTime, endTime)
      : calculateWorkingHours(startTime, endTime);

    if (duration <= 0) {
      throw new Error('年假时间必须大于0');
    }

    // 检查年假余额是否足够
    if (duration > this.data.annualLeave.remainingHours) {
      throw new Error(`年假余额不足。需要${duration}小时，剩余${this.data.annualLeave.remainingHours}小时`);
    }

    const record = {
      id: this.generateId(),
      startTime: new Date(startTime).toISOString(),
      endTime: new Date(endTime).toISOString(),
      duration,
      calculationType, // 保存计算类型以便兼容
      type: '年假',
      createdAt: new Date().toISOString()
    };

    // 添加年假记录
    this.data.annualLeaveRecords.push(record);

    // 更新年假使用情况
    this.data.annualLeave.usedHours += duration;
    this.data.annualLeave.remainingHours -= duration;

    this.saveToLocalStorage(); // 自动保存
    this.notifyListeners('ANNUAL_LEAVE_RECORD_ADDED', {
      annualLeaveRecord: record,
      updatedAnnualLeave: this.data.annualLeave
    });

    return record;
  }

  /**
   * 删除年假记录
   * 连带删除相关的调休记录（如果是纯年假消费）
   */
  deleteAnnualLeaveRecord(id) {
    const index = this.data.annualLeaveRecords.findIndex(r => r.id === id);

    if (index === -1) {
      throw new Error('年假记录不存在');
    }

    const record = this.data.annualLeaveRecords[index];

    // 查找需要连带删除的调休记录
    const restRecordsToDelete = [];
    let relatedConsumptions = [];

    // 情况1：通过调休自动扣除的年假记录
    if (record.source === '调休自动扣除' && record.relatedRestTime) {
      const [restStartTime, restEndTime] = record.relatedRestTime.split('_');

      // 查找对应的调休记录
      const restRecord = this.data.restRecords.find(r =>
        r.txbegintime === restStartTime &&
        r.txendtime === restEndTime
      );

      if (restRecord) {
        // 查找这个调休记录的所有消费记录
        const allConsumptionsForRest = this.data.consumptionRecords.filter(c =>
          c.restStartTime === restRecord.txbegintime &&
          c.restEndTime === restRecord.txendtime
        );

        // 对于调休自动扣除的年假记录，无论是纯年假还是混合消费，
        // 都应该连带删除调休记录，因为它们是强绑定关系
        restRecordsToDelete.push(restRecord);
        relatedConsumptions = allConsumptionsForRest;
      }
    } else {
      // 情况2：手动添加的年假记录
      relatedConsumptions = this.data.consumptionRecords.filter(c =>
        c.type === '年假' &&
        c.consumptionStartTime === record.startTime &&
        c.consumptionEndTime === record.endTime
      );

      for (const consumption of relatedConsumptions) {
        // 查找对应的调休记录
        const restRecord = this.data.restRecords.find(r =>
          r.txbegintime === consumption.restStartTime &&
          r.txendtime === consumption.restEndTime
        );

        if (restRecord) {
          // 检查这个调休记录是否只使用年假（纯年假消费）
          const allConsumptionsForRest = this.data.consumptionRecords.filter(c =>
            c.restStartTime === restRecord.txbegintime &&
            c.restEndTime === restRecord.txendtime
          );

          const hasOvertimeConsumption = allConsumptionsForRest.some(c => c.type === '调休');

          // 如果只有年假消费，没有加班时间消费，则连带删除调休记录
          if (!hasOvertimeConsumption) {
            restRecordsToDelete.push(restRecord);
          }
        }
      }
    }

    // 执行删除操作
    this.data.annualLeaveRecords.splice(index, 1);

    // 删除相关的消费记录
    this.data.consumptionRecords = this.data.consumptionRecords.filter(c =>
      !relatedConsumptions.includes(c)
    );

    // 删除调休记录并归还加班时间
    for (const restRecord of restRecordsToDelete) {
      const restIndex = this.data.restRecords.findIndex(r => r.id === restRecord.id);
      if (restIndex !== -1) {
        this.data.restRecords.splice(restIndex, 1);
      }
    }

    // 重新计算加班记录的剩余时间（归还加班时间）
    this.data.overtimeRecords = updateOvertimeRecordsRemainingTime(
      this.data.overtimeRecords,
      this.data.consumptionRecords
    );

    // 更新年假使用情况
    let deletedAnnualLeaveHours = 0;

    if (record.source === '调休自动扣除') {
      // 对于调休自动扣除的年假，直接使用记录的duration
      deletedAnnualLeaveHours = record.duration;
    } else {
      // 对于手动添加的年假，直接使用记录的duration
      // 因为手动添加的年假记录不会创建消费记录，所以直接使用duration
      deletedAnnualLeaveHours = record.duration || 0;
    }

    if (deletedAnnualLeaveHours > 0) {
      this.data.annualLeave.usedHours -= deletedAnnualLeaveHours;
      this.data.annualLeave.remainingHours += deletedAnnualLeaveHours;
    }

    this.saveToLocalStorage(); // 自动保存

    // 通知监听器
    this.notifyListeners('ANNUAL_LEAVE_RECORD_DELETED', {
      annualLeaveRecord: record,
      deletedRestRecords: restRecordsToDelete,
      deletedConsumptions: relatedConsumptions,
      restoredHours: deletedAnnualLeaveHours
    });

    return {
      annualLeaveRecord: record,
      deletedRestRecords: restRecordsToDelete,
      deletedConsumptions: relatedConsumptions,
      restoredHours: deletedAnnualLeaveHours
    };
  }

  // ==================== 数据持久化 ====================

  /**
   * 保存数据到本地存储
   */
  saveToLocalStorage(key = 'overtimeData') {
    try {
      const dataToSave = exportData(this.data, 'v2');
      const encryptedData = encryptRC4(JSON.stringify(dataToSave), 'Legna');
      localStorage.setItem(key, encryptedData);
      
      this.notifyListeners('DATA_SAVED', { key, timestamp: new Date().toISOString() });
      
      return true;
    } catch (error) {
      console.error('保存数据失败:', error);
      throw new Error('保存数据失败: ' + error.message);
    }
  }

  /**
   * 从本地存储加载数据（支持向后兼容）
   */
  loadFromLocalStorage(key = 'overtimeData') {
    try {
      // 首先尝试加载新格式的数据（RC4加密）
      const encryptedData = localStorage.getItem(key);

      if (encryptedData) {
        try {
          const decryptedData = decryptRC4(encryptedData, 'Legna');
          const parsedData = JSON.parse(decryptedData);

          this.setAllData(parsedData);
          this.notifyListeners('DATA_LOADED', { key, timestamp: new Date().toISOString() });

          console.log('成功加载新格式数据');
          return true;
        } catch (decryptError) {
          console.log('新格式数据解密失败，尝试加载旧格式数据');
        }
      }

      // 尝试加载旧格式的数据（未加密，分别存储）
      const legacyData = this.loadLegacyData();
      if (legacyData) {
        this.setAllData(legacyData);

        // 将旧数据迁移到新格式并保存
        this.saveToLocalStorage();

        this.notifyListeners('DATA_LOADED', {
          key: 'legacy_migration',
          timestamp: new Date().toISOString(),
          migrated: true
        });

        console.log('成功迁移旧格式数据到新格式');
        return true;
      }

      throw new Error('没有找到保存的数据');

    } catch (error) {
      console.error('加载数据失败:', error);
      throw new Error('加载数据失败: ' + error.message);
    }
  }

  /**
   * 加载旧格式的数据（向后兼容）
   */
  loadLegacyData() {
    try {
      const overtimeRecords = localStorage.getItem('overtimeRecords');
      const consumptionRecords = localStorage.getItem('consumptionRecords');
      const restRecords = localStorage.getItem('restRecords');
      const annualLeave = localStorage.getItem('annualLeave');
      const annualLeaveRecords = localStorage.getItem('annualLeaveRecords');

      // 检查是否有任何旧数据
      if (!overtimeRecords && !restRecords && !annualLeave && !annualLeaveRecords) {
        return null;
      }

      console.log('发现旧格式数据，开始迁移...');

      // 解析旧数据
      const legacyData = {
        overtimeRecords: overtimeRecords ? JSON.parse(overtimeRecords) : [],
        consumptionRecords: consumptionRecords ? JSON.parse(consumptionRecords) : [],
        restRecords: restRecords ? JSON.parse(restRecords) : [],
        annualLeave: annualLeave ? JSON.parse(annualLeave) : { totalDays: 0, remainingHours: 0 },
        annualLeaveRecords: annualLeaveRecords ? JSON.parse(annualLeaveRecords) : []
      };

      // 数据格式转换和验证
      const migratedData = this.migrateLegacyData(legacyData);

      // 清理旧的localStorage键（可选，为了避免混乱）
      // localStorage.removeItem('overtimeRecords');
      // localStorage.removeItem('consumptionRecords');
      // localStorage.removeItem('restRecords');
      // localStorage.removeItem('annualLeave');
      // localStorage.removeItem('annualLeaveRecords');

      return migratedData;

    } catch (error) {
      console.error('加载旧格式数据失败:', error);
      return null;
    }
  }

  /**
   * 迁移旧格式数据到新格式
   */
  migrateLegacyData(legacyData) {
    const migratedData = {
      overtimeRecords: [],
      restRecords: [],
      annualLeaveRecords: [],
      consumptionRecords: legacyData.consumptionRecords || [],
      annualLeave: this.migrateLegacyAnnualLeave(legacyData.annualLeave)
    };

    // 迁移加班记录
    if (legacyData.overtimeRecords) {
      migratedData.overtimeRecords = legacyData.overtimeRecords.map(record => ({
        ...record,
        id: record.id || this.generateId(),
        createdAt: record.createdAt || new Date().toISOString()
      }));
    }

    // 迁移调休记录
    if (legacyData.restRecords) {
      migratedData.restRecords = legacyData.restRecords.map(record => ({
        ...record,
        id: record.id || this.generateId(),
        createdAt: record.createdAt || new Date().toISOString()
      }));
    }

    // 迁移年假记录
    if (legacyData.annualLeaveRecords) {
      migratedData.annualLeaveRecords = legacyData.annualLeaveRecords.map(record => ({
        ...record,
        id: record.id || this.generateId(),
        createdAt: record.createdAt || new Date().toISOString()
      }));
    }

    console.log('数据迁移完成:', {
      overtimeRecords: migratedData.overtimeRecords.length,
      restRecords: migratedData.restRecords.length,
      annualLeaveRecords: migratedData.annualLeaveRecords.length,
      consumptionRecords: migratedData.consumptionRecords.length
    });

    return migratedData;
  }

  /**
   * 迁移旧版年假数据到新格式
   */
  migrateLegacyAnnualLeave(legacyAnnualLeave) {
    if (!legacyAnnualLeave) {
      return {
        totalDays: 0,
        totalHours: 0,
        usedHours: 0,
        remainingHours: 0
      };
    }

    // 如果已经有完整的新格式数据（包含 totalHours），直接使用
    if (typeof legacyAnnualLeave.totalHours === 'number' && legacyAnnualLeave.totalHours >= 0) {
      const totalHours = legacyAnnualLeave.totalHours;
      const totalDays = legacyAnnualLeave.totalDays || Math.floor(totalHours / 8);
      const usedHours = legacyAnnualLeave.usedHours || 0;
      const remainingHours = totalHours - usedHours;

      console.log('年假数据已是新格式:', {
        totalDays, totalHours, usedHours, remainingHours
      });

      return {
        totalDays,
        totalHours,
        usedHours: Math.max(0, usedHours),
        remainingHours: Math.max(0, remainingHours)
      };
    }

    // 旧版本只有 totalDays（按天数 * 8小时计算）
    const totalDays = legacyAnnualLeave.totalDays || 0;
    const totalHours = totalDays * 8; // 每天8小时

    // 旧版的 remainingHours 可能是基于天数计算的，需要验证合理性
    let remainingHours = legacyAnnualLeave.remainingHours;

    // 如果 remainingHours 超过 totalHours，说明是旧格式数据错误，需要修正
    if (remainingHours === undefined || remainingHours > totalHours) {
      remainingHours = totalHours;
    }

    const usedHours = totalHours - remainingHours;

    console.log('迁移年假数据:', {
      旧数据: legacyAnnualLeave,
      新数据: { totalDays, totalHours, usedHours, remainingHours }
    });

    return {
      totalDays,
      totalHours,
      usedHours: Math.max(0, usedHours),
      remainingHours: Math.max(0, remainingHours)
    };
  }

  /**
   * 导出数据为文件
   */
  exportToFile(format = 'v2') {
    const dataToExport = exportData(this.data, format);
    const encryptedData = encryptRC4(JSON.stringify(dataToExport), 'Legna');
    
    const blob = new Blob([encryptedData], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `牛马数据_${new Date().toISOString().split('T')[0]}.牛马`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    
    URL.revokeObjectURL(url);
    
    this.notifyListeners('DATA_EXPORTED', { format, timestamp: new Date().toISOString() });
  }

  /**
   * 从文件导入数据
   */
  async importFromFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const encryptedData = e.target.result;
          const decryptedData = decryptRC4(encryptedData, 'Legna');
          const parsedData = JSON.parse(decryptedData);
          
          this.setAllData(parsedData);

          // 保存到localStorage
          this.saveToLocalStorage();

          this.notifyListeners('DATA_IMPORTED', {
            filename: file.name,
            timestamp: new Date().toISOString()
          });

          resolve(true);
        } catch (error) {
          console.error('导入数据失败:', error);
          reject(new Error('导入数据失败: ' + error.message));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('文件读取失败'));
      };
      
      reader.readAsText(file);
    });
  }

  /**
   * 清理数据
   */
  cleanupData() {
    const result = cleanupData(this.data);
    this.data = result.data;
    
    this.notifyListeners('DATA_CLEANED', { cleanedCount: result.cleanedCount });
    
    return result;
  }

  /**
   * 生成唯一ID
   */
  generateId() {
    // 使用更高精度的时间戳和更长的随机字符串确保唯一性
    return Date.now().toString() + '-' + Math.random().toString(36).substr(2, 12) + '-' + Math.random().toString(36).substr(2, 6);
  }

  // ==================== 数据清理 ====================

  /**
   * 清理无效数据
   */
  cleanupInvalidData() {
    let cleanupCount = 0;

    // 清理无效的加班记录
    const validOvertimeRecords = this.data.overtimeRecords.filter(record => {
      const startDate = new Date(record.startTime);
      const endDate = new Date(record.endTime);
      const isValid = !isNaN(startDate.getTime()) && !isNaN(endDate.getTime()) && record.duration > 0;
      if (!isValid) {
        console.log('Removing invalid overtime record:', record);
        cleanupCount++;
      }
      return isValid;
    });

    // 清理无效的调休记录
    const validRestRecords = this.data.restRecords.filter(record => {
      const startDate = new Date(record.txbegintime);
      const endDate = new Date(record.txendtime);
      const isValid = !isNaN(startDate.getTime()) && !isNaN(endDate.getTime()) && record.duration > 0;
      if (!isValid) {
        console.log('Removing invalid rest record:', record);
        cleanupCount++;
      }
      return isValid;
    });

    // 清理无效的年假记录
    const validAnnualLeaveRecords = this.data.annualLeaveRecords.filter(record => {
      const startDate = new Date(record.startTime);
      const endDate = new Date(record.endTime);
      const isValid = !isNaN(startDate.getTime()) && !isNaN(endDate.getTime()) && record.duration > 0;
      if (!isValid) {
        console.log('Removing invalid annual leave record:', record);
        cleanupCount++;
      }
      return isValid;
    });

    // 更新数据
    this.data.overtimeRecords = validOvertimeRecords;
    this.data.restRecords = validRestRecords;
    this.data.annualLeaveRecords = validAnnualLeaveRecords;

    if (cleanupCount > 0) {
      this.saveToLocalStorage();
      this.notifyListeners();
    }

    return cleanupCount;
  }
}

// 创建全局数据管理器实例
export const dataManager = new DataManager();
